import { store } from "../state/store.js";
import { filteredTransponders, programCount } from "../core/filter.js";
import { tpKey } from "../core/tpKey.js";
import { matchBy4Fields, ownFavKeys } from "../core/favorites.js";
import { FREE_TO_AIR_DE, SKY_DE, HDPLUS, TELEVES_K20_16 } from "../constants/favorites.js";
import { getIntermediateFrequencyMHz, getPolBandLabel } from "../utils/sat.js";
import { estimateBitrateMbps, getBitrateRowTitle, isHighBitrateTp } from "../utils/bitrate.js";
import { appendButton } from "../utils/render.js";
import { updateFavBadges } from "./render/favoritesTable.js";

export const MOBILE_BREAKPOINT = 700;
const MOBILE_FAV_TABS = new Set(["fta", "sky", "hd", "k20", "own"]);

const orientationMq = window.matchMedia("(orientation: landscape)");
const PHONE_UA_RE = /(iPhone|iPod|Android.*Mobile|Windows Phone)/i;
const TOUCH_MQ = window.matchMedia("(pointer: coarse)");

function isPhoneSizedScreen() {
  const sw = window.screen?.width || window.innerWidth;
  const sh = window.screen?.height || window.innerHeight;
  return Math.min(sw, sh) <= MOBILE_BREAKPOINT;
}

function isLikelyPhoneDevice() {
  const ua = navigator.userAgent || "";
  const uaDataMobile = navigator.userAgentData && navigator.userAgentData.mobile === true;
  return uaDataMobile || PHONE_UA_RE.test(ua);
}

function isTouchDevice() {
  return (
    (typeof navigator.maxTouchPoints === "number" && navigator.maxTouchPoints > 0) ||
    TOUCH_MQ.matches ||
    isLikelyPhoneDevice()
  );
}

export function isMobileLayout() {
  return isPhoneSizedScreen() || (isTouchDevice() && !orientationMq.matches);
}

export function isMobileLandscape() {
  return (isLikelyPhoneDevice() || isPhoneSizedScreen()) && orientationMq.matches;
}

export function syncMobileBodyClass() {
  document.body.classList.toggle("mobile-layout", isMobileLayout());
}

export function syncMobileOrientationClass() {
  document.body.classList.toggle("mobile-landscape", isMobileLandscape());
}

export function setupMobileLayout(onChange) {
  syncMobileBodyClass();
  syncMobileOrientationClass();

  const handler = () => {
    syncMobileBodyClass();
    syncMobileOrientationClass();
    if (typeof onChange === "function") onChange();
  };

  window.addEventListener("resize", handler, { passive: true });
  window.addEventListener("orientationchange", handler, { passive: true });

  return handler;
}

export function setupMobileOrientationGuard() {
  syncMobileOrientationClass();

  const handler = () => {
    syncMobileOrientationClass();
  };

  if (typeof orientationMq.addEventListener === "function") {
    orientationMq.addEventListener("change", handler);
  } else if (typeof orientationMq.addListener === "function") {
    orientationMq.addListener(handler);
  }
  window.addEventListener("resize", handler, { passive: true });
  window.addEventListener("orientationchange", handler, { passive: true });

  return handler;
}

function metaItem(label, value) {
  const wrap = document.createElement("div");
  wrap.className = "meta-item";

  const lab = document.createElement("span");
  lab.className = "meta-label";
  lab.textContent = label;
  wrap.appendChild(lab);

  const val = document.createElement("span");
  val.className = "meta-value";
  val.textContent = String(value ?? "");
  wrap.appendChild(val);

  return wrap;
}

function programPill(text, styleText = "") {
  const pill = document.createElement("span");
  pill.className = "pill";
  pill.textContent = String(text ?? "");
  if (styleText) pill.style.cssText = styleText;
  return pill;
}

function bitrateBadge(tp) {
  const mbps = estimateBitrateMbps(tp);
  if (!Number.isFinite(mbps) || mbps <= 54) return null;

  const pill = document.createElement("span");
  pill.className = "pill";
  pill.style.cssText = "background:#F39200;color:white;border-color:#F39200;font-weight:700;";
  pill.textContent = `${Math.round(mbps)}+ MBit`;
  pill.title = getBitrateRowTitle(tp);
  return pill;
}

function parameterToggleLabel(isOpen) {
  return isOpen ? "Param -" : "Param +";
}

function renderProgramList(key) {
  const programs = store.programsByTpKey.get(key) || [];
  const wrap = document.createElement("div");
  wrap.className = "mobile-details";

  const title = document.createElement("div");
  title.className = "pill info";
  title.textContent = `Programme (${programs.length})`;
  wrap.appendChild(title);

  const list = document.createElement("div");
  list.className = "mobile-programs";
  wrap.appendChild(list);

  if (!programs.length) {
    const empty = document.createElement("div");
    empty.className = "muted";
    empty.style.marginTop = "8px";
    empty.textContent = "Keine Programme gefunden.";
    wrap.appendChild(empty);
    return wrap;
  }

  const limit = 24;
  for (const p of programs.slice(0, limit)) {
    const item = document.createElement("div");
    item.className = "mobile-program";

    const top = document.createElement("div");
    top.className = "mobile-program-top";
    item.appendChild(top);

    const name = document.createElement("div");
    name.className = "mobile-program-name";
    name.textContent = p.program_name || p.name || p.service_name || "Unbekannt";
    top.appendChild(name);

    const sid = document.createElement("div");
    sid.className = "pill";
    sid.textContent = p.sid != null && p.sid !== "" ? `SID ${p.sid}` : "SID -";
    top.appendChild(sid);

    const meta = document.createElement("div");
    meta.className = "mobile-program-meta";
    meta.appendChild(programPill(p.language || "Sprache -"));
    meta.appendChild(programPill(p.type || "Typ -"));
    meta.appendChild(programPill(p.resolution || "Aufloesung -"));
    meta.appendChild(
      programPill(
        p.encrypted ? "verschluesselt" : "frei",
        p.encrypted
          ? "background:#F39200;color:white;border-color:#F39200"
          : "background:#8E8E8E;color:white;border-color:#8E8E8E"
      )
    );
    item.appendChild(meta);

    list.appendChild(item);
  }

  if (programs.length > limit) {
    const more = document.createElement("div");
    more.className = "muted";
    more.style.marginTop = "8px";
    more.textContent = `Weitere ${programs.length - limit} Programme ausgeblendet.`;
    wrap.appendChild(more);
  }

  return wrap;
}

function renderMetaSection(entries, paramsOpen) {
  const meta = document.createElement("div");
  meta.className = "mobile-meta";
  meta.style.display = paramsOpen ? "grid" : "none";
  meta.setAttribute("aria-hidden", paramsOpen ? "false" : "true");

  for (const [label, value] of entries) {
    meta.appendChild(metaItem(label, value));
  }

  return meta;
}

function renderMobileTpCard(tp) {
  const key = tpKey(tp);
  const isOpen = store.openDetails.has(key);
  const selected = store.selection.has(key);
  const starred = store.starred.has(key);
  const highBitrate = isHighBitrateTp(tp);
  const progCount = programCount(tp);

  const card = document.createElement("article");
  card.className = "mobile-card";
  if (highBitrate) {
    card.classList.add("high-bitrate");
    card.title = getBitrateRowTitle(tp);
  }

  const head = document.createElement("div");
  head.className = "mobile-card-head";
  card.appendChild(head);

  const left = document.createElement("div");
  head.appendChild(left);

  const titleRow = document.createElement("div");
  titleRow.className = "mobile-title-row";
  left.appendChild(titleRow);

  const title = document.createElement("div");
  title.className = "mobile-card-title";
  title.textContent = tp.tp_name || tp.provider || key;
  titleRow.appendChild(title);

  const bitrate = bitrateBadge(tp);
  if (bitrate) titleRow.appendChild(bitrate);

  const subtitle = document.createElement("div");
  subtitle.className = "mobile-card-sub";
  const polBand = getPolBandLabel(tp.pol, tp.frequency_MHz) || "";
  const zf = getIntermediateFrequencyMHz(tp.frequency_MHz) || "";
  subtitle.textContent = [tp.sat_pos, tp.frequency_MHz ? `${tp.frequency_MHz} MHz` : "", zf ? `ZF ${zf}` : "", polBand, tp.symbolrate_kSps ? `SR ${tp.symbolrate_kSps}` : ""].filter(Boolean).join(" · ");
  left.appendChild(subtitle);

  const paramsOpen = !isMobileLayout() || store.openMeta.has(key);
  const badges = document.createElement("div");
  badges.className = "mobile-card-badges";
  badges.appendChild(programPill(`Prog. ${progCount}`, "background:rgba(56,189,248,.14);border-color:rgba(56,189,248,.28)"));
  badges.appendChild(programPill(tp.sat_pos || "Sat -"));
  if (isMobileLayout()) {
    appendButton(badges, {
      label: parameterToggleLabel(paramsOpen),
      className: "pill info param-toggle param-badge",
      dataset: { act: "toggle_meta", key },
      title: "Technische Parameter ein- oder ausblenden",
    });
  }
  head.appendChild(badges);

  const meta = renderMetaSection(
    [
      ["Satellit", tp.sat_pos || "—"],
      ["Frequenz", tp.frequency_MHz || "—"],
      ["Polarisation", polBand || "—"],
      ["DVB", tp.dvb || "—"],
      ["TSID/ONID", tp.tsid_onid || (tp.tsid ? `${tp.tsid}/${tp.onid || ""}` : "—")],
      ["SR", tp.symbolrate_kSps || "—"]
    ],
    paramsOpen
  );
  card.appendChild(meta);

  const actions = document.createElement("div");
  actions.className = "mobile-card-actions";
  card.appendChild(actions);

  appendButton(actions, {
    label: selected ? "Ausgewählt" : "Auswahl",
    className: selected ? "primary" : "",
    dataset: { act: "toggle_sel", key },
  });
  appendButton(actions, {
    label: starred ? "Favorit" : "Merken",
    className: starred ? "primary" : "",
    dataset: { act: "toggle_star", key },
  });
    appendButton(actions, {
    label: isOpen ? "Details verbergen" : "Details anzeigen",
    className: "small-action",
    dataset: { act: "toggle_details", key },
    title: "Programme ein- oder ausblenden",
  });

  if (isOpen) {
    card.appendChild(renderProgramList(key));
  }

  return card;
}

function renderFavCard(fav, hit, key, groupLabel) {
  const tp = hit || null;
  const isOpen = !!key && store.openDetails.has(key);
  const selected = !!key && store.selection.has(key);
  const starred = !!key && store.starred.has(key);
  const progCount = !!key ? programCount(tp) : 0;
  const paramsOpen = !isMobileLayout() || (key ? store.openMeta.has(key) : false);

  const card = document.createElement("article");
  card.className = "mobile-card";
  if (tp && isHighBitrateTp(tp)) {
    card.classList.add("high-bitrate");
    card.title = getBitrateRowTitle(tp);
  }

  const head = document.createElement("div");
  head.className = "mobile-card-head";
  card.appendChild(head);

  const left = document.createElement("div");
  head.appendChild(left);

  const titleRow = document.createElement("div");
  titleRow.className = "mobile-title-row";
  left.appendChild(titleRow);

  const title = document.createElement("div");
  title.className = "mobile-card-title";
  title.textContent = tp ? (tp.tp_name || tp.provider || key) : groupLabel;
  titleRow.appendChild(title);

  const bitrate = tp ? bitrateBadge(tp) : null;
  if (bitrate) titleRow.appendChild(bitrate);

  const subtitle = document.createElement("div");
  subtitle.className = "mobile-card-sub";
  subtitle.textContent = tp
    ? [tp.sat_pos, tp.frequency_MHz ? `${tp.frequency_MHz} MHz` : "", getPolBandLabel(tp.pol, tp.frequency_MHz), tp.symbolrate_kSps ? `SR ${tp.symbolrate_kSps}` : ""].filter(Boolean).join(" · ")
    : [fav.sat_pos, fav.frequency_MHz ? `${fav.frequency_MHz} MHz` : "", getPolBandLabel(fav.pol, fav.frequency_MHz), fav.symbolrate_kSps ? `SR ${fav.symbolrate_kSps}` : ""].filter(Boolean).join(" · ");
  left.appendChild(subtitle);

  const badges = document.createElement("div");
  badges.className = "mobile-card-badges";
  badges.appendChild(programPill(tp ? `Prog. ${progCount}` : "nicht gefunden", tp ? "background:rgba(56,189,248,.14);border-color:rgba(56,189,248,.28)" : "background:#F39200;color:white;border-color:#F39200"));
  badges.appendChild(programPill(key ? "gefunden" : "kein Match"));
  if (isMobileLayout()) {
    appendButton(badges, {
      label: parameterToggleLabel(paramsOpen),
      className: "pill info param-toggle param-badge",
      disabled: !key,
      dataset: { act: "toggle_meta", key },
      title: "Technische Parameter ein- oder ausblenden",
    });
  }
  head.appendChild(badges);

  const meta = renderMetaSection(
    [
      ["Satellit", tp?.sat_pos || fav.sat_pos || "—"],
      ["Frequenz", tp?.frequency_MHz || fav.frequency_MHz || "—"],
      ["Polarisation", getPolBandLabel(tp?.pol || fav.pol, tp?.frequency_MHz || fav.frequency_MHz) || "—"],
      ["DVB", tp?.dvb || "—"],
      ["TSID/ONID", tp?.tsid_onid || (tp?.tsid ? `${tp.tsid}/${tp.onid || ""}` : "—")],
      ["SR", tp?.symbolrate_kSps || fav.symbolrate_kSps || "—"]
    ],
    paramsOpen
  );
  card.appendChild(meta);

  const actions = document.createElement("div");
  actions.className = "mobile-card-actions";
  card.appendChild(actions);

  appendButton(actions, {
    label: selected ? "Ausgewählt" : "Auswahl",
    className: selected ? "primary" : "",
    disabled: !key,
    dataset: { act: "toggle_sel", key },
  });
  appendButton(actions, {
    label: starred ? "Favorit" : "Merken",
    className: starred ? "primary" : "",
    disabled: !key,
    dataset: { act: "toggle_star", key },
  });
    appendButton(actions, {
    label: isOpen ? "Details verbergen" : "Details anzeigen",
    className: "small-action",
    disabled: !key,
    dataset: { act: "toggle_details", key },
  });

  if (!key) {
    const missing = document.createElement("div");
    missing.className = "mobile-details";

    const pill = document.createElement("span");
    pill.className = "pill";
    pill.style.cssText = "background:#F39200;color:white;border-color:#F39200";
    pill.textContent = "fehlt in DB";
    missing.appendChild(pill);

    const note = document.createElement("div");
    note.className = "muted";
    note.style.marginTop = "8px";
    note.textContent = `${groupLabel}: dieser Favorit konnte in der Datenbank nicht eindeutig gefunden werden.`;
    missing.appendChild(note);

    card.appendChild(missing);
    return card;
  }

  if (isOpen) {
    card.appendChild(renderProgramList(key));
  }

  return card;
}

function renderList(containerId, list, renderer) {
  const container = document.getElementById(containerId);
  if (!container) return;
  container.innerHTML = "";

  if (!list.length) {
    const empty = document.createElement("div");
    empty.className = "mobile-card";
    const text = document.createElement("div");
    text.className = "muted";
    text.textContent = "Keine Einträge vorhanden.";
    empty.appendChild(text);
    container.appendChild(empty);
    return;
  }

  for (const item of list) {
    container.appendChild(renderer(item));
  }
}

function renderMobileTransponders() {
  renderList("mobileTpList", filteredTransponders(), (tp) => renderMobileTpCard(tp));
}

function renderMobileSelection() {
  const list = [];
  for (const [key] of store.selection) {
    const tp = store.tpByKey.get(key);
    if (tp) list.push(tp);
  }

  const container = document.getElementById("mobileSelList");
  if (!container) return;
  container.innerHTML = "";

  if (!list.length) {
    const empty = document.createElement("div");
    empty.className = "mobile-card";
    const text = document.createElement("div");
    text.className = "muted";
    text.textContent = "Keine Transponder ausgewählt.";
    empty.appendChild(text);
    container.appendChild(empty);
    return;
  }

  for (const tp of list) {
    const card = renderMobileTpCard(tp);
    const actions = card.querySelector(".mobile-card-actions");
    if (actions) {
      actions.innerHTML = "";
      const key = tpKey(tp);
      const starred = store.starred.has(key);
      const isOpen = store.openDetails.has(key);

      appendButton(actions, {
        label: "Entfernen",
        className: "primary",
        dataset: { act: "remove", key },
      });
      appendButton(actions, {
        label: starred ? "Favorit" : "Merken",
        className: starred ? "primary" : "",
        dataset: { act: "toggle_star", key },
      });
      appendButton(actions, {
        label: isOpen ? "Details verbergen" : "Details anzeigen",
        className: "small-action",
        dataset: { act: "toggle_details", key },
      });
    }
    container.appendChild(card);
  }
}

function renderFixedFavorites(containerId, favList, groupLabel) {
  const container = document.getElementById(containerId);
  if (!container) return;
  container.innerHTML = "";

  if (!favList.length) {
    const empty = document.createElement("div");
    empty.className = "mobile-card";
    const text = document.createElement("div");
    text.className = "muted";
    text.textContent = "Keine Favoriten-Einträge vorhanden.";
    empty.appendChild(text);
    container.appendChild(empty);
    return;
  }

  for (const fav of favList) {
    const hit = store.transponders.find((t) => matchBy4Fields(t, fav));
    const key = hit ? (hit.tp_key || tpKey(hit)) : "";
    const card = renderFavCard(fav, hit, key, groupLabel);
    container.appendChild(card);
  }
}

function renderMobileFavoritesActive() {
  renderFixedFavorites("mobileFtaList", FREE_TO_AIR_DE, "Free to Air - Deutsch");
  renderFixedFavorites("mobileSkyList", SKY_DE, "Sky Deutschland");
  renderFixedFavorites("mobileHdList", HDPLUS, "HD+");
  renderFixedFavorites("mobileK20List", TELEVES_K20_16, "Televes K20-16");
  renderOwnFavorites();
}

function renderOwnFavorites() {
  const container = document.getElementById("mobileOwnList");
  if (!container) return;
  container.innerHTML = "";

  const keys = ownFavKeys();
  if (!keys.length) {
    const empty = document.createElement("div");
    empty.className = "mobile-card";
    const text = document.createElement("div");
    text.className = "muted";
    text.textContent = "Noch keine eigenen Favoriten gespeichert.";
    empty.appendChild(text);
    container.appendChild(empty);
    return;
  }

  for (const key of keys) {
    const tp = store.tpByKey.get(key);
    if (!tp) continue;

    const card = renderMobileTpCard(tp);
    const actions = card.querySelector(".mobile-card-actions");
    if (actions) {
      actions.innerHTML = "";
      const isOpen = store.openDetails.has(key);

      appendButton(actions, {
        label: "Auswahl",
        className: store.selection.has(key) ? "primary" : "",
        dataset: { act: "toggle_sel", key },
      });
      appendButton(actions, {
        label: "Entfernen",
        dataset: { act: "unstarOwn", key },
      });
      appendButton(actions, {
        label: isOpen ? "Details verbergen" : "Details anzeigen",
        className: "small-action",
        dataset: { act: "toggle_details", key },
      });
    }
    container.appendChild(card);
  }
}

function resolveMobileFavTab() {
  const tab = String(store.ui?.favTab || "fta");
  if (!MOBILE_FAV_TABS.has(tab)) return "fta";
  return tab;
}

export function syncMobileFavVisibility(tab = store.ui?.favTab || "fta") {
  const activeTab = resolveMobileFavTab(tab);
  const map = {
    fta: "favFtaWrap",
    sky: "favSkyWrap",
    hd: "favHdWrap",
    k20: "favK20Wrap",
    own: "favOwnWrap"
  };

  for (const [k, id] of Object.entries(map)) {
    const node = document.getElementById(id);
    if (node) node.style.display = k === activeTab ? "" : "none";
  }

  return activeTab;
}

export function renderMobileViews() {
  renderMobileTransponders();
  renderMobileSelection();
  const active = resolveMobileFavTab();
  if (store.ui) store.ui.favTab = active;
  syncMobileFavVisibility(active);
  renderMobileFavoritesActive();
  updateFavBadges();
}
